const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const usdWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const count = new Intl.NumberFormat('en-US');

/** Positive = money out. Money in is shown with a minus sign. */
export function money(amount: number): string {
  return amount < 0 ? `−${usd.format(-amount)}` : usd.format(amount);
}

/** Totals in summaries: whole dollars. */
export function moneyWhole(amount: number): string {
  return usdWhole.format(amount);
}

export function num(n: number): string {
  return count.format(n);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${num(n)} ${n === 1 ? one : many}`;
}

/** "Oct 5, 2:00 PM" (kept for places that want the full stamp). */
export function when(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "10:00 AM". */
export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/** Today: "10:00 AM". Otherwise "Sep 28". */
export function shortStamp(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return sameDay(d, now) ? clock(iso) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Home's recent activity: "10:00" today, "Fri" this week, else "Sep 28". */
export function relativeStamp(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (sameDay(d, now)) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: false });
  if (now.getTime() - d.getTime() < 6 * 86_400_000) return d.toLocaleDateString('en-US', { weekday: 'short' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Activity timeline: "Oct 05 10:00". */
export function stamp(iso: string): string {
  const d = new Date(iso);
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const day = String(d.getDate()).padStart(2, '0');
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${month} ${day} ${time}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A bank or Salesforce date (YYYY-MM-DD) as "Mar 02". No time zone shift: it's a calendar date. */
export function day(isoDate: string): string {
  const [, m, d] = isoDate.split('-');
  const month = MONTHS[Number(m) - 1];
  return month && d ? `${month} ${d}` : isoDate;
}

/** "Sep 28" from a full ISO time or a date. */
export function monthDay(iso: string | null | undefined): string {
  if (!iso) return 'never';
  return day(iso.slice(0, 10));
}

/** Whole days between a YYYY-MM-DD date and today. */
export function ageInDays(isoDate: string, now = new Date()): number {
  const [y, m, d] = isoDate.split('-').map(Number);
  const then = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((today - then) / 86_400_000));
}

/** YYYY-MM-DD for `days` before today (local calendar). */
export function daysAgo(days: number, now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 1 → "1st", 22 → "22nd", 13 → "13th". */
export function ordinal(n: number): string {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/** "··5052". */
export function maskLabel(mask: string | null | undefined): string {
  return mask ? `··${mask}` : '';
}

/** "alice@example.com" reads as "alice" in sentences; other labels as they are. */
export function person(label: string): string {
  return label.includes('@') ? label.split('@')[0] : label;
}

export const RULE_LABELS: Record<string, string> = {
  'one-to-one': 'One-to-one',
  late: 'Logged late',
  split: 'Several charges',
  combined: 'Combined',
  batch: 'Batch',
  'held-check': 'Held check',
  ai: 'AI suggestion',
};
